import { Text, useTexture } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { Group, MathUtils, Mesh, RepeatWrapping, SRGBColorSpace } from "three";
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { getViewportTier } from "../../lib/viewport";
import { getAboutSceneLayout, type Vec3Tuple } from "./aboutSceneLayout";

type AboutSceneProps = {
  weight: number;
  reducedMotion: boolean;
};

type StoneConfig = {
  position: Vec3Tuple;
  scale: Vec3Tuple;
  rotation: Vec3Tuple;
};

type AboutSceneTextProps = {
  children: ReactNode;
  position: Vec3Tuple;
  rotation?: Vec3Tuple;
  fontSize: number;
  maxWidth?: number;
  opacity: number;
  color?: string;
};

const stones: StoneConfig[] = [
  { position: [0, -0.62, 0], scale: [0.72, 0.2, 0.46], rotation: [0.02, 0.1, -0.04] },
  { position: [0.02, -0.32, 0.01], scale: [0.54, 0.16, 0.36], rotation: [-0.02, -0.16, 0.03] },
  { position: [-0.01, -0.08, 0], scale: [0.42, 0.13, 0.3], rotation: [0.02, 0.22, 0.02] },
  { position: [0.03, 0.12, 0.01], scale: [0.32, 0.11, 0.23], rotation: [-0.01, -0.12, -0.02] },
  { position: [-0.02, 0.29, 0], scale: [0.24, 0.085, 0.18], rotation: [0.02, 0.18, 0.03] },
  { position: [0.01, 0.43, 0], scale: [0.17, 0.065, 0.13], rotation: [-0.02, -0.22, 0.01] },
  { position: [0, 0.54, 0], scale: [0.1, 0.04, 0.08], rotation: [0.01, 0.12, -0.02] }
];

function AboutSceneText({
  children,
  color = "#17151b",
  fontSize,
  maxWidth,
  opacity,
  position,
  rotation = [0, 0, 0]
}: AboutSceneTextProps) {
  return (
    <Text
      anchorX="center"
      anchorY="middle"
      color={color}
      fillOpacity={opacity}
      fontSize={fontSize}
      fontWeight={800}
      lineHeight={0.92}
      maxWidth={maxWidth}
      position={position}
      rotation={rotation}
      textAlign="center"
    >
      {children}
    </Text>
  );
}

export function AboutScene({ reducedMotion, weight }: AboutSceneProps) {
  const viewport = useThree((state) => getViewportTier(state.size.width));
  const stoneTexture = useTexture("/images/textures/stone/Stone1.png");
  const groupRef = useRef<Group>(null);
  const stackRef = useRef<Group>(null);
  const titleRef = useRef<Group>(null);
  const coreRef = useRef<Group>(null);
  const floorRef = useRef<Mesh>(null);
  const stoneRefs = useRef<(Mesh | null)[]>([]);
  const visible = weight > 0.001;
  const opacity = Math.min(weight, 1);
  const mobile = viewport === "mobile";
  const layout = getAboutSceneLayout(viewport);

  const ringScales = useMemo(() => [0.78, 1.06, 1.32], []);

  useEffect(() => {
    stoneTexture.colorSpace = SRGBColorSpace;
    stoneTexture.wrapS = RepeatWrapping;
    stoneTexture.wrapT = RepeatWrapping;
    stoneTexture.repeat.set(1.65, 1.65);
    stoneTexture.needsUpdate = true;
  }, [stoneTexture]);

  useFrame((state, delta) => {
    if (!groupRef.current || !stackRef.current || !titleRef.current || !coreRef.current || !floorRef.current) {
      return;
    }

    const elapsed = state.clock.elapsedTime;
    const idle = reducedMotion ? 0 : 1;
    const pointerX = reducedMotion ? 0 : state.pointer.x;
    const pointerY = reducedMotion ? 0 : state.pointer.y;

    groupRef.current.position.x = MathUtils.damp(
      groupRef.current.position.x,
      layout.basePosition[0] + pointerX * (mobile ? 0.045 : 0.08),
      4.2,
      delta
    );
    groupRef.current.position.y = MathUtils.damp(
      groupRef.current.position.y,
      layout.basePosition[1] + pointerY * (mobile ? 0.025 : 0.05) + Math.sin(elapsed * 0.22) * 0.025 * idle,
      4.2,
      delta
    );
    groupRef.current.position.z = MathUtils.damp(groupRef.current.position.z, layout.basePosition[2], 4.2, delta);
    groupRef.current.rotation.y = MathUtils.damp(groupRef.current.rotation.y, pointerX * -0.055, 4.2, delta);
    groupRef.current.scale.x = MathUtils.damp(groupRef.current.scale.x, layout.baseScale, 4.2, delta);
    groupRef.current.scale.y = MathUtils.damp(groupRef.current.scale.y, layout.baseScale, 4.2, delta);
    groupRef.current.scale.z = MathUtils.damp(groupRef.current.scale.z, layout.baseScale, 4.2, delta);

    stackRef.current.rotation.y = MathUtils.damp(
      stackRef.current.rotation.y,
      -0.12 + pointerX * 0.055 + Math.sin(elapsed * 0.12) * 0.035 * idle,
      4.4,
      delta
    );
    stackRef.current.rotation.x = MathUtils.damp(stackRef.current.rotation.x, pointerY * 0.025, 4.4, delta);

    titleRef.current.rotation.y = MathUtils.damp(
      titleRef.current.rotation.y,
      layout.titleRotation[1] + pointerX * 0.055,
      4.4,
      delta
    );
    titleRef.current.rotation.x = MathUtils.damp(
      titleRef.current.rotation.x,
      layout.titleRotation[0] + pointerY * -0.018,
      4.4,
      delta
    );

    coreRef.current.scale.y = MathUtils.damp(
      coreRef.current.scale.y,
      reducedMotion ? 1 : 1 + Math.sin(elapsed * 1.8) * 0.08,
      5.2,
      delta
    );

    stoneRefs.current.forEach((stone, index) => {
      if (!stone) return;

      const config = stones[index];

      // Different speed and direction per stone
      const direction = index % 2 === 0 ? 1 : -1;
      const speed = 0.18 + index * 0.035;
      const offset = index * 0.9;

      stone.rotation.x = MathUtils.damp(
        stone.rotation.x,
        config.rotation[0] + Math.sin(elapsed * speed + offset) * 0.04 * idle,
        4.8,
        delta
      );

      stone.rotation.y = MathUtils.damp(
        stone.rotation.y,
        config.rotation[1] + elapsed * speed * direction * idle,
        4.8,
        delta
      );

      stone.rotation.z = MathUtils.damp(
        stone.rotation.z,
        config.rotation[2] + Math.cos(elapsed * speed + offset) * 0.035 * idle,
        4.8,
        delta
      );
    });

    floorRef.current.rotation.z = reducedMotion ? 0 : elapsed * 0.035;
  });

  return (
    <group ref={groupRef} position={layout.basePosition} scale={layout.baseScale} visible={visible}>
      <pointLight color="#5ddff0" distance={4.2} intensity={(mobile ? 0.95 : 1.35) * opacity} position={[0.04, 0.08, 0.56]} />
      <pointLight color="#fff8ef" distance={5.6} intensity={(mobile ? 0.5 : 0.72) * opacity} position={[-0.8, 1.6, 1.5]} />

      <group ref={titleRef} position={layout.titlePosition} rotation={layout.titleRotation}>
        <AboutSceneText
          color="#050505"
          fontSize={layout.titleFontSize}
          maxWidth={layout.titleMaxWidth}
          opacity={0.18 * opacity}
          position={[0.045, -0.055, -0.1]}
          rotation={[0, 0, 0]}
        >
          ABOUT 7 STONES
        </AboutSceneText>
        <AboutSceneText
          fontSize={layout.titleFontSize}
          maxWidth={layout.titleMaxWidth}
          opacity={(mobile ? 0.72 : 0.82) * opacity}
          position={[0, 0, 0]}
          rotation={[0, 0, 0]}
        >
          ABOUT 7 STONES
        </AboutSceneText>
      </group>

      <group ref={stackRef} position={layout.stackPosition} scale={layout.stackScale} rotation={[0.02, -0.12, 0]}>
        <group ref={coreRef}>
          <mesh position={[0.01, -0.04, 0.7]} scale={[0.022, 1.1, 0.022]}>
            <cylinderGeometry args={[1, 1, 1, 16]} />
            <meshBasicMaterial color="#52dff2" depthWrite={false} transparent opacity={0.72 * opacity} />
          </mesh>
          <mesh position={[0.01, -0.04, 0.395]} scale={[0.075, 1.08, 0.075]}>
            <cylinderGeometry args={[1, 1, 1, 24]} />
            <meshBasicMaterial color="#7ff2ff" depthWrite={false} transparent opacity={0.12 * opacity} />
          </mesh>
        </group>

        {stones.map((stone, index) => (
          <mesh
            key={`about-stone-${index}`}
            ref={(node) => {
              stoneRefs.current[index] = node;
            }}
            position={stone.position}
            rotation={stone.rotation}
            scale={stone.scale}
          >
            <sphereGeometry args={[1, 36, 18]} />
            <meshStandardMaterial
              color={index < 2 ? "#bbb3a8" : "#cbc3b8"}
              map={stoneTexture}
              metalness={0.02}
              opacity={0.98 * opacity}
              roughness={0.82}
              transparent
            />
          </mesh>
        ))}
      </group>

      <mesh ref={floorRef} position={layout.floorPosition} rotation={[-Math.PI / 2, 0, 0]} scale={layout.floorScale}>
        <circleGeometry args={[1, 96]} />
        <meshBasicMaterial color="#2a2521" depthWrite={false} transparent opacity={0.055 * opacity} />
      </mesh>

      {ringScales.map((scale, index) => (
        <mesh
          key={`about-floor-ring-${scale}`}
          position={[layout.floorPosition[0], layout.floorPosition[1] + 0.006 + index * 0.004, layout.floorPosition[2]]}
          rotation={[-Math.PI / 2, 0, index * 0.08]}
          scale={[layout.floorScale[0] * scale, layout.floorScale[1] * scale, 1]}
        >
          <torusGeometry args={[1, 0.006, 8, 96]} />
          <meshBasicMaterial color={index === 0 ? "#54ddea" : "#2b2927"} depthWrite={false} transparent opacity={(index === 0 ? 0.22 : 0.1) * opacity} />
        </mesh>
      ))}
    </group>
  );
}
